/**
 * SpawnSystem — shape-based bloom spawning.
 *
 * Instead of continuously topping the dish up to a target density, we wait
 * for the field to drain to BLOOM_THRESHOLD, then spawn a *shape* of food
 * (circle / line / zigzag / spiral / scatter) trickled in over BLOOM_DURATION.
 *
 * Every shape shares a bloomId; when every member of a bloom is absorbed,
 * the player gets a bonus DNA drop + audio cue. This creates a little
 * spatial puzzle: sweeping a shape in one pass keeps the combo alive much
 * more reliably than panic-hunting scattered crumbs.
 */

import type { Container } from 'pixi.js';
import type { World } from '@/core/ECS';
import { Absorbable, Hostile, PlayerCell, Position, Powerup, Renderable } from '@/components';
import type { FoodDef, PredatorDef } from '@/data/types';
import { rand, randRange, weightedPick } from '@/utils/rng';
import { createFood } from '@/entities/createFood';
import { createPredator, type PredatorStatMul } from '@/entities/createPredator';
import { createPowerup } from '@/entities/createPowerup';
import { bus } from '@/core/EventBus';
import { player } from './PlayerState';
import type { EnvironmentSystem } from './EnvironmentSystem';

const TARGET_FOOD_DENSITY = 0.00012;  // food per px^2
const BLOOM_DURATION = 0.5;           // seconds to trickle bloom in (short so shape forms before drifting)
const BLOOM_MAX_LIFETIME = 35;        // seconds before a bloom record expires
const SHAPE_MIN_COUNT = 8;            // below this, fall back to scatter
const BLOOM_DRIFT_SCALE = 0.8;        // bloom food drifts briskly — shape scatters if you're slow

// satellite (small bonus) shapes — spawned periodically while the main bloom
// is active. They give a smaller reward and don't advance combo, but do
// extend the combo timer, so grabbing them buys you breathing room.
const SATELLITE_INTERVAL_MIN = 3;
const SATELLITE_INTERVAL_MAX = 6;
const SATELLITE_COUNT_MIN = 6;
const SATELLITE_COUNT_MAX = 10;

// Tolerance for "main bloom cleared": once most of the shape has been
// absorbed and enough time has passed, remaining stragglers (food that
// drifted across edges or wrapped around) are auto-destroyed and the
// bloom counts as cleared — so combo isn't lost chasing the last 5%.
const MAIN_NEAR_CLEAR_RATIO = 0.92;
const MAIN_NEAR_CLEAR_MIN_AGE = 6;

type ShapeId = 'circle' | 'line' | 'zigzag' | 'spiral' | 'star' | 'rectangle' | 'scatter';
const SHAPE_POOL: ShapeId[] = ['circle', 'line', 'zigzag', 'spiral', 'star', 'rectangle'];

// Powerup spawn tuning. Bombs are rare pressure-release items — they only
// start appearing once the player has grown past BOMB_MIN_BIOMASS and the
// field has at least BOMB_MIN_PREDATORS predators. Shields are more common
// and gate on a lower biomass threshold.
const BOMB_MIN_BIOMASS = 1500;
const BOMB_MIN_PREDATORS = 4;
const BOMB_INTERVAL_MIN = 45;
const BOMB_INTERVAL_MAX = 90;

const SHIELD_MIN_BIOMASS = 300;
const SHIELD_INTERVAL_MIN = 22;
const SHIELD_INTERVAL_MAX = 40;

const POWERUP_EDGE_PAD = 80;

export class SpawnSystem {
  private predatorTimer = 0;
  private maxPredators = 8;
  private bombTimer = BOMB_INTERVAL_MIN;
  private shieldTimer = SHIELD_INTERVAL_MIN;
  // Gate used by the new-game intro: while disabled, no food/predator/powerup
  // spawns happen. CellStage flips this back on after the scripted reveal.
  private enabled = true;

  // bloom state
  private bloomQueue: { x: number; y: number; bloomId: number }[] = [];
  private bloomRate = 0;
  private bloomAccum = 0;
  private nextBloomId = 1;
  private activeBlooms = new Map<number, { remaining: number; total: number; shape: ShapeId; cx: number; cy: number; age: number; tier: 'main' | 'satellite' }>();
  private satelliteTimer = SATELLITE_INTERVAL_MIN;

  constructor(
    private world: World,
    private parent: Container,
    private foodDefs: Record<string, FoodDef>,
    private predatorDefs: Record<string, PredatorDef>,
    private env: EnvironmentSystem,
  ) {
    bus.on('food:absorbed', ({ bloomId, x, y }) => {
      if (bloomId == null) return;
      const b = this.activeBlooms.get(bloomId);
      if (!b) return;
      b.remaining--;
      if (b.remaining <= 0) {
        this.activeBlooms.delete(bloomId);
        bus.emit('bloom:cleared', { bloomId, x, y, shape: b.shape, tier: b.tier });
      }
    });
  }

  setEnabled(b: boolean): void { this.enabled = b; }

  update(dt: number, width: number, height: number): void {
    if (!this.enabled) return;
    // age blooms and handle near-clear / orphan-timeout
    for (const [id, b] of this.activeBlooms) {
      b.age += dt;
      // Main bloom near-clear: once 92%+ eaten and enough time has elapsed,
      // destroy any stragglers and emit bloom:cleared — stops the player
      // from losing combo chasing food that's wrapped around the screen.
      if (
        b.tier === 'main' &&
        b.age > MAIN_NEAR_CLEAR_MIN_AGE &&
        b.remaining > 0 &&
        (b.total - b.remaining) / b.total >= MAIN_NEAR_CLEAR_RATIO
      ) {
        this.forceClearBloom(id, b);
        continue;
      }
      if (b.age > BLOOM_MAX_LIFETIME) {
        this.forceClearBloom(id, b, { silent: true });
      }
    }

    // --- food: shape-bloom spawning ---
    // Main shape: only one on screen at a time. Next spawns after the
    // previous is fully cleared (or orphan-timed-out).
    const hasMain = this.hasMainBloom();
    if (this.bloomQueue.length === 0 && !hasMain) {
      const n = Math.floor(width * height * TARGET_FOOD_DENSITY);
      if (n > 0) this.buildBloom(width, height, n, 'main');
    }

    // Satellite shapes: small bonus clusters that drift in while the main
    // is being worked. They give less reward (see ComboSystem / CellStage).
    if (hasMain) {
      this.satelliteTimer -= dt;
      if (this.satelliteTimer <= 0) {
        this.spawnSatellite(width, height);
        this.satelliteTimer = SATELLITE_INTERVAL_MIN + rand() * (SATELLITE_INTERVAL_MAX - SATELLITE_INTERVAL_MIN);
      }
    } else {
      // reset so satellite doesn't immediately fire when the next main spawns
      this.satelliteTimer = SATELLITE_INTERVAL_MIN;
    }

    // trickle the bloom queue
    if (this.bloomQueue.length > 0) {
      this.bloomAccum += this.bloomRate * dt;
      let k = Math.floor(this.bloomAccum);
      if (k > 0) {
        this.bloomAccum -= k;
        while (k > 0 && this.bloomQueue.length > 0) {
          const pos = this.bloomQueue.shift()!;
          const def = this.pickFoodDef();
          if (def) {
            createFood(this.world, this.parent, def, pos.x, pos.y, {
              bloomId: pos.bloomId,
              driftScale: BLOOM_DRIFT_SCALE,
            });
          }
          k--;
        }
      }
    }

    // --- predators ---
    // Threat scales with the player's upgraded size + total biomass. As the
    // player grows, more predators spawn more often, and the spawn pool skews
    // toward tougher species (dinoflagellates over paramecia).
    this.predatorTimer -= dt;
    let predCount = 0;
    for (const _ of this.world.query([Hostile])) predCount++;
    const suppress = this.env.getEffects().suppressPredators === true;

    const threat = this.computeThreat();
    // Floor of ~8 from the start so the screen feels populated; scales with
    // screen area + threat. Most predators won't aggro until the player gets
    // close (see MovementSystem AGGRO_RANGE), so a full field is ambient
    // pressure, not a death sentence. Cap ramps to ~32 at the 250k stage
    // target via threat (which now uses 250k as its log10 base).
    const baseMax = 8 + Math.floor((width * height) / 500_000);
    const threatBoost = Math.floor(threat * 18);
    this.maxPredators = Math.min(36, baseMax + threatBoost);

    if (!suppress && this.predatorTimer <= 0 && predCount < this.maxPredators) {
      this.spawnPredator(width, height, threat);
      // Fast initial fill: when below the floor, spawn rapidly so the screen
      // populates in the first few seconds. Above floor, fall back to the
      // slower trickle that scales with threat.
      if (predCount + 1 < baseMax) {
        this.predatorTimer = 0.3 + rand() * 0.4;
      } else {
        const base = 12 + rand() * 8;
        this.predatorTimer = base * (1 - 0.55 * threat);
        if (threat > 0.5 && predCount + 1 < this.maxPredators && rand() < threat * 0.35) {
          this.spawnPredator(width, height, threat);
        }
      }
    } else if (suppress) {
      this.predatorTimer = 2;
    }

    // --- powerups ---
    this.updatePowerups(dt, width, height, predCount);
  }

  private updatePowerups(dt: number, width: number, height: number, predCount: number): void {
    // age existing powerups; destroy when they time out
    const expired: number[] = [];
    for (const e of this.world.query([Powerup])) {
      const pw = this.world.get(e, Powerup)!;
      pw.age += dt;
      if (pw.age >= pw.lifetime) expired.push(e);
    }
    for (const id of expired) {
      const r = this.world.get(id, Renderable);
      if (r) r.root.parent?.removeChild(r.root);
      this.world.destroyEntity(id);
    }

    // count current powerups to avoid stacking multiple at once
    let bombCount = 0, shieldCount = 0;
    for (const e of this.world.query([Powerup])) {
      const pw = this.world.get(e, Powerup)!;
      if (pw.kind === 'bomb') bombCount++;
      else shieldCount++;
    }

    // shield — gated on biomass, spawns anywhere on screen
    this.shieldTimer -= dt;
    if (shieldCount === 0 && this.shieldTimer <= 0 && player.biomass >= SHIELD_MIN_BIOMASS) {
      const pos = this.pickPowerupSpawn(width, height);
      createPowerup(this.world, this.parent, 'shield', pos.x, pos.y);
      bus.emit('powerup:spawned', { kind: 'shield', x: pos.x, y: pos.y });
      this.shieldTimer = SHIELD_INTERVAL_MIN + rand() * (SHIELD_INTERVAL_MAX - SHIELD_INTERVAL_MIN);
    }

    // bomb — rarer, only when the field is actually threatening
    this.bombTimer -= dt;
    if (bombCount === 0 && this.bombTimer <= 0
        && player.biomass >= BOMB_MIN_BIOMASS
        && predCount >= BOMB_MIN_PREDATORS) {
      const pos = this.pickPowerupSpawn(width, height);
      createPowerup(this.world, this.parent, 'bomb', pos.x, pos.y);
      bus.emit('powerup:spawned', { kind: 'bomb', x: pos.x, y: pos.y });
      this.bombTimer = BOMB_INTERVAL_MIN + rand() * (BOMB_INTERVAL_MAX - BOMB_INTERVAL_MIN);
    }
  }

  private pickPowerupSpawn(width: number, height: number): { x: number; y: number } {
    return {
      x: POWERUP_EDGE_PAD + rand() * (width - POWERUP_EDGE_PAD * 2),
      y: POWERUP_EDGE_PAD + rand() * (height - POWERUP_EDGE_PAD * 2),
    };
  }

  /** 0 = fresh run, 1 = deep into upgrades. Blends cell size and biomass.
   *  Biomass ramp targets the 250k stage goal so escalation is steady
   *  through the whole run rather than maxing out in early-mid game. */
  private computeThreat(): number {
    const radiusProg = Math.min(1, Math.max(0, (player.stats.cellRadius - 18) / 30));
    const biomassProg = Math.min(1, Math.log10(1 + player.biomass) / Math.log10(1 + 250_000));
    return Math.min(1, 0.5 * radiusProg + 0.5 * biomassProg);
  }

  spawnInitial(width: number, height: number): void {
    const n = Math.floor(width * height * TARGET_FOOD_DENSITY);
    this.buildBloom(width, height, n, 'main');
  }

  private hasMainBloom(): boolean {
    for (const b of this.activeBlooms.values()) if (b.tier === 'main') return true;
    return false;
  }

  private buildBloom(w: number, h: number, count: number, tier: 'main' | 'satellite'): void {
    const bloomId = this.nextBloomId++;
    const shape: ShapeId = count >= SHAPE_MIN_COUNT
      ? SHAPE_POOL[Math.floor(rand() * SHAPE_POOL.length)]
      : 'scatter';
    const S = Math.min(w, h) * 0.85;
    const positions = generateShapePoints(shape, count, S);

    // Main shape spawns anywhere on screen (left / center / right) while
    // staying fully visible: compute the shape's bbox and pick a center
    // uniformly within the valid range.
    const { cx, cy } = pickCenterForShape(positions, w, h, 40, 18);

    this.activeBlooms.set(bloomId, { remaining: count, total: count, shape, cx, cy, age: 0, tier });

    for (const [ox, oy] of positions) {
      const jx = (rand() - 0.5) * 18;
      const jy = (rand() - 0.5) * 18;
      let x = cx + ox + jx;
      let y = cy + oy + jy;
      if (x < 30) x = 30; else if (x > w - 30) x = w - 30;
      if (y < 30) y = 30; else if (y > h - 30) y = h - 30;
      this.bloomQueue.push({ x, y, bloomId });
    }

    this.bloomRate = count / BLOOM_DURATION;
    this.bloomAccum = 0;
  }

  private spawnSatellite(w: number, h: number): void {
    // Satellite = a miniature version of the main shape pool at ~35% size.
    // Can land anywhere on screen (fully-visible clamp applies) — previously
    // constrained to a ring off-center, which made them feel scripted.
    const bloomId = this.nextBloomId++;
    const count = SATELLITE_COUNT_MIN + Math.floor(rand() * (SATELLITE_COUNT_MAX - SATELLITE_COUNT_MIN + 1));
    const shape: ShapeId = SHAPE_POOL[Math.floor(rand() * SHAPE_POOL.length)];
    const S = Math.min(w, h) * 0.35;
    const positions = generateShapePoints(shape, count, S);
    const { cx, cy } = pickCenterForShape(positions, w, h, 40, 10);

    this.activeBlooms.set(bloomId, { remaining: count, total: count, shape, cx, cy, age: 0, tier: 'satellite' });

    for (const [ox, oy] of positions) {
      const jx = (rand() - 0.5) * 10;
      const jy = (rand() - 0.5) * 10;
      let x = cx + ox + jx;
      let y = cy + oy + jy;
      if (x < 30) x = 30; else if (x > w - 30) x = w - 30;
      if (y < 30) y = 30; else if (y > h - 30) y = h - 30;
      const def = this.pickFoodDef();
      if (!def) continue;
      // spawn immediately (no trickle) so satellites pop in visibly
      createFood(this.world, this.parent, def, x, y, {
        bloomId,
        driftScale: BLOOM_DRIFT_SCALE * 0.5,
      });
    }

    bus.emit('audio:cue', { id: 'satelliteSpawn' });
  }

  private forceClearBloom(
    id: number,
    b: { cx: number; cy: number; shape: ShapeId; tier: 'main' | 'satellite' },
    opts: { silent?: boolean } = {},
  ): void {
    // destroy any remaining food entities belonging to this bloom
    const toDestroy: number[] = [];
    for (const e of this.world.query([Absorbable])) {
      const fa = this.world.get(e, Absorbable)!;
      if (fa.bloomId === id) toDestroy.push(e);
    }
    for (const ent of toDestroy) {
      const r = this.world.get(ent, Renderable);
      if (r) r.root.parent?.removeChild(r.root);
      this.world.destroyEntity(ent);
    }
    this.activeBlooms.delete(id);
    if (!opts.silent) {
      bus.emit('bloom:cleared', { bloomId: id, x: b.cx, y: b.cy, shape: b.shape, tier: b.tier });
    }
  }

  private spawnPredator(width: number, height: number, threat = 0): void {
    // At threat=0, use base spawnWeights. At threat=1, bias toward tougher
    // predators (weight scaled by hp/40 — amoeba=1x, paramecium=~0.6x,
    // dinoflagellate=~2x). Smooth interpolation in between.
    // Filter out predators the player hasn't reached the biomass threshold
    // for yet — late-game variants only enter the pool once player has
    // grown past their unlockBiomass milestone.
    const eligible = Object.values(this.predatorDefs)
      .filter(def => (def.unlockBiomass ?? 0) <= player.biomass);
    const weighted = eligible.map(def => {
      const toughness = def.hp / 40;
      return { def, weight: def.spawnWeight * (1 + threat * (toughness - 1)) };
    });
    const picked = weightedPick(weighted, 'weight');
    if (!picked) return;
    const def = picked.def;

    // Scatter spawn: evaluate all four edges and score each by distance from
    // existing predators + distance from the player-center. Pick the best
    // edge with a bit of randomness so spawns feel spread out, not scripted.
    let px = width * 0.5, py = height * 0.5;
    const existing: { x: number; y: number }[] = [];
    for (const e of this.world.query([Hostile, Position])) {
      const p = this.world.get(e, Position)!;
      existing.push({ x: p.x, y: p.y });
    }
    // player position — the current pack tends to converge on the player,
    // so spawning on the far side gives the field visual breathing room
    for (const e of this.world.query([PlayerCell, Position])) {
      const p = this.world.get(e, Position)!;
      px = p.x; py = p.y; break;
    }

    const m = 60;
    const candidates: { x: number; y: number; score: number }[] = [];
    for (let i = 0; i < 5; i++) {
      const side = Math.floor(rand() * 4);
      let cx = 0, cy = 0;
      switch (side) {
        case 0: cx = -m; cy = rand() * height; break;
        case 1: cx = width + m; cy = rand() * height; break;
        case 2: cx = rand() * width; cy = -m; break;
        case 3: cx = rand() * width; cy = height + m; break;
      }
      // score = min distance to nearest existing predator + player offset
      let nearest = Infinity;
      for (const p of existing) {
        const d = Math.hypot(cx - p.x, cy - p.y);
        if (d < nearest) nearest = d;
      }
      if (existing.length === 0) nearest = 1000;
      const playerDist = Math.hypot(cx - px, cy - py);
      candidates.push({ x: cx, y: cy, score: nearest + playerDist * 0.4 });
    }
    candidates.sort((a, b) => b.score - a.score);
    const pick = candidates[0];
    const mul = this.difficultyMult();

    // Swarm species spawn as a clustered group instead of singletons. Each
    // member is jittered around the spawn point so they read as a school
    // rather than a stacked column.
    const count = def.swarmCount ?? 1;
    for (let i = 0; i < count; i++) {
      const jx = count > 1 ? (rand() - 0.5) * 80 : 0;
      const jy = count > 1 ? (rand() - 0.5) * 80 : 0;
      createPredator(this.world, this.parent, def, pick.x + jx, pick.y + jy, mul);
    }
  }

  /**
   * Per-stat multipliers applied at spawn time. Smoothly ramps from 1× at
   * biomass 0 to roughly 2.6× hp / 1.5× damage / 1.18× speed/radius at the
   * stage target (250k biomass). Existing predators keep their stats —
   * scaling only affects future spawns, so the field gradually grows
   * tougher as the player upgrades, instead of suddenly buffing mid-fight.
   */
  private difficultyMult(): PredatorStatMul {
    const t = Math.min(1, Math.log10(1 + Math.max(0, player.biomass)) / Math.log10(1 + 250_000));
    return {
      hp: 1 + t * 1.6,
      damage: 1 + t * 0.5,
      speed: 1 + t * 0.18,
      radius: 1 + t * 0.18,
    };
  }

  private pickFoodDef(): FoodDef | null {
    const effects = this.env.getEffects();
    const multipliers = effects.spawnMultipliers ?? {};
    const list = Object.values(this.foodDefs).map(def => ({
      def,
      weight: def.rarity * (multipliers[def.id] ?? 1),
    }));
    const picked = weightedPick(list, 'weight');
    return picked?.def ?? null;
  }

  private _rangeCheck(): void { void randRange; }
}

// --- shape generators: all return offsets around (0,0) ---

function generateShapePoints(shape: ShapeId, count: number, S: number): [number, number][] {
  switch (shape) {
    case 'circle':    return circleShape(count, S);
    case 'line':      return lineShape(count, S);
    case 'zigzag':    return zigzagShape(count, S);
    case 'spiral':    return spiralShape(count, S);
    case 'star':      return starShape(count, S);
    case 'rectangle': return rectangleShape(count, S);
    case 'scatter':
    default:          return scatterShape(count, S);
  }
}

/**
 * Pick a center (cx, cy) such that every offset in `positions` — plus
 * `jitter` slack and `pad` from the screen edge — lies inside [0, w] × [0, h].
 * If the shape is too big to fit (unlikely at normal sizes), falls back to
 * the screen center.
 */
function pickCenterForShape(
  positions: [number, number][],
  w: number,
  h: number,
  pad: number,
  jitter: number,
): { cx: number; cy: number } {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const [ox, oy] of positions) {
    if (ox < minX) minX = ox;
    if (ox > maxX) maxX = ox;
    if (oy < minY) minY = oy;
    if (oy > maxY) maxY = oy;
  }
  minX -= jitter; maxX += jitter; minY -= jitter; maxY += jitter;
  const cxMin = pad - minX;
  const cxMax = w - pad - maxX;
  const cyMin = pad - minY;
  const cyMax = h - pad - maxY;
  const cx = cxMax > cxMin ? cxMin + rand() * (cxMax - cxMin) : w * 0.5;
  const cy = cyMax > cyMin ? cyMin + rand() * (cyMax - cyMin) : h * 0.5;
  return { cx, cy };
}

function circleShape(count: number, S: number): [number, number][] {
  const r = S * 0.38;
  const pts: [number, number][] = [];
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2;
    pts.push([Math.cos(a) * r, Math.sin(a) * r]);
  }
  return pts;
}

function lineShape(count: number, S: number): [number, number][] {
  const angle = rand() * Math.PI * 2;
  const len = S * 0.85;
  const dx = Math.cos(angle), dy = Math.sin(angle);
  const pts: [number, number][] = [];
  for (let i = 0; i < count; i++) {
    const t = count > 1 ? (i / (count - 1)) - 0.5 : 0;
    pts.push([dx * len * t, dy * len * t]);
  }
  return pts;
}

function zigzagShape(count: number, S: number): [number, number][] {
  const angle = rand() * Math.PI * 2;
  const len = S * 0.85;
  const amp = S * 0.08;
  const dx = Math.cos(angle), dy = Math.sin(angle);
  const nx = -dy, ny = dx;
  const pts: [number, number][] = [];
  for (let i = 0; i < count; i++) {
    const t = count > 1 ? (i / (count - 1)) - 0.5 : 0;
    const zz = (i % 2 === 0 ? 1 : -1) * amp;
    pts.push([dx * len * t + nx * zz, dy * len * t + ny * zz]);
  }
  return pts;
}

function spiralShape(count: number, S: number): [number, number][] {
  const startR = S * 0.42;
  const turns = 3;
  const pts: [number, number][] = [];
  for (let i = 0; i < count; i++) {
    const t = count > 1 ? i / (count - 1) : 0;
    const a = t * Math.PI * 2 * turns;
    const r = startR * (1 - t * 0.85);
    pts.push([Math.cos(a) * r, Math.sin(a) * r]);
  }
  return pts;
}

function starShape(count: number, S: number): [number, number][] {
  const points = 5;
  const outerR = S * 0.45;
  const innerR = S * 0.18;
  const rot = rand() * Math.PI * 2;
  const pts: [number, number][] = [];
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + rot;
    const phase = ((a - rot) * points) / Math.PI;
    const tri = Math.abs(((phase % 2) + 2) % 2 - 1);
    const r = innerR + (outerR - innerR) * tri;
    pts.push([Math.cos(a) * r, Math.sin(a) * r]);
  }
  return pts;
}

function rectangleShape(count: number, S: number): [number, number][] {
  const rw = S * (0.6 + rand() * 0.3);
  const rh = S * (0.3 + rand() * 0.2);
  const perim = 2 * (rw + rh);
  const angle = rand() * Math.PI * 2;
  const cos = Math.cos(angle), sin = Math.sin(angle);
  const pts: [number, number][] = [];
  for (let i = 0; i < count; i++) {
    const s = (i / count) * perim;
    let x = 0, y = 0;
    if (s < rw)              { x = -rw / 2 + s;              y = -rh / 2; }
    else if (s < rw + rh)    { x = rw / 2;                   y = -rh / 2 + (s - rw); }
    else if (s < 2 * rw + rh){ x = rw / 2 - (s - rw - rh);   y = rh / 2; }
    else                     { x = -rw / 2;                  y = rh / 2 - (s - 2 * rw - rh); }
    pts.push([x * cos - y * sin, x * sin + y * cos]);
  }
  return pts;
}

function scatterShape(count: number, S: number): [number, number][] {
  const pts: [number, number][] = [];
  for (let i = 0; i < count; i++) {
    const a = rand() * Math.PI * 2;
    const r = 30 + rand() * S * 0.4;
    pts.push([Math.cos(a) * r, Math.sin(a) * r]);
  }
  return pts;
}
